<template>
  <!--
    Apply a Stripe coupon code. Without a weekDate it goes on the customer the
    way signup applies it (a gift card becomes credit, anything else their
    discount); with a weekDate it goes on that week's invoice only, which is
    reissued with it. The code is checked first and what will happen is shown
    before the apply button is enabled.
  -->
  <v-dialog :value="value" max-width="640px" persistent @input="$emit('input', $event)">
    <v-card>
      <v-card-title>
        <v-icon class="mr-2">mdi-ticket-percent-outline</v-icon>
        {{ weekDate ? 'Add Coupon to Invoice' : 'Apply Coupon to Customer' }}
        <v-spacer/>
        <v-btn icon :disabled="applying" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        <div class="mb-3">
          <strong>{{ customerName }}</strong>
          <span v-if="weekDate"> - week of {{ weekDate }}</span>
        </div>
        <v-text-field
          v-model="code"
          label="Coupon code"
          placeholder="FED10, a gift card code, a referral code"
          outlined
          dense
          autofocus
          :disabled="applying"
          :error-messages="lookupError ? [lookupError] : []"
          @input="check = null; lookupError = null"
          @keydown.enter="lookup"
        >
          <template v-slot:append-outer>
            <v-btn outlined :loading="checking" :disabled="!code || applying" @click="lookup">Check</v-btn>
          </template>
        </v-text-field>

        <template v-if="check">
          <v-simple-table v-if="check.coupon" dense class="mb-3">
            <tbody>
            <tr>
              <td class="text--secondary">Code</td>
              <td><code>{{ check.coupon.id }}</code></td>
            </tr>
            <tr v-if="check.coupon.name">
              <td class="text--secondary">Name</td>
              <td>{{ check.coupon.name }}</td>
            </tr>
            <tr>
              <td class="text--secondary">Kind</td>
              <td>{{ check.kind }}<span v-if="check.on_old_account_only"> (old Stripe account only)</span></td>
            </tr>
            <tr>
              <td class="text--secondary">Value</td>
              <td>{{ valueText(check.coupon) }}</td>
            </tr>
            <tr>
              <td class="text--secondary">Duration</td>
              <td>{{ durationText(check.coupon) }}</td>
            </tr>
            <tr>
              <td class="text--secondary">Redemptions</td>
              <td>
                {{ check.coupon.times_redeemed }}<span v-if="check.coupon.max_redemptions != null"> of {{ check.coupon.max_redemptions }}</span>
                <span v-if="check.coupon.redeem_by"> - valid until {{ dateFormatted(check.coupon.redeem_by * 1000, {formatString: 'll'}) }}</span>
              </td>
            </tr>
            </tbody>
          </v-simple-table>

          <v-alert v-for="(problem, i) in check.problems" :key="`p${i}`" dense outlined type="error" class="mb-2">
            {{ problem }}
          </v-alert>
          <v-alert v-for="(note, i) in check.notes" :key="`n${i}`" dense outlined type="info" class="mb-2">
            {{ note }}
          </v-alert>
        </template>
        <div v-else class="caption text--secondary">
          Check the code to see what it is and what applying it will do.
        </div>

        <v-textarea
          v-model="reason"
          class="mt-3"
          label="Reason (why, required)"
          rows="2"
          auto-grow
          hint="Saved as a CS note on the customer, with the coupon's details"
          persistent-hint
          :disabled="applying"
        />

        <v-alert v-if="applyError" dense outlined type="error" class="mt-2">{{ applyError }}</v-alert>
      </v-card-text>
      <v-card-actions>
        <v-spacer/>
        <v-btn text :disabled="applying" @click="close">Cancel</v-btn>
        <v-btn color="primary" :disabled="!check || !check.can_apply || !reason.trim()" :loading="applying" @click="apply">
          {{ weekDate ? 'Add to Invoice' : 'Apply to Customer' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import api from '@/api';
import {dateFormatted, formatCurrency} from '@/store/utils';

export default {
  name: 'CouponApplyDialog',
  props: {
    value: {type: Boolean, default: false},
    uid: {type: String, required: true},
    customerName: {type: String, default: ''},
    // set to apply to this week's invoice instead of the customer
    weekDate: {type: String, default: null},
  },
  data() {
    return {
      code: '',
      reason: '',
      check: null,
      checking: false,
      lookupError: null,
      applying: false,
      applyError: null,
    };
  },
  watch: {
    value(open) {
      if (open) {
        this.code = '';
        this.reason = '';
        this.check = null;
        this.lookupError = null;
        this.applyError = null;
      }
    },
  },
  methods: {
    dateFormatted,

    close() {
      this.$emit('input', false);
    },

    async lookup() {
      const code = (this.code || '').trim();
      if (!code) return;
      this.checking = true;
      this.lookupError = null;
      this.applyError = null;
      try {
        const target = this.weekDate ? 'invoice' : 'customer';
        const {data} = await api.get(`v2/stripe/coupons/check/${encodeURIComponent(code)}`, {params: {target}});
        this.check = data;
      } catch (e) {
        this.lookupError = this.messageOf(e, 'Could not check the coupon');
      } finally {
        this.checking = false;
      }
    },

    async apply() {
      const code = (this.code || '').trim();
      const reason = this.reason.trim();
      this.applying = true;
      this.applyError = null;
      try {
        let data;
        if (this.weekDate) {
          ({data} = await api.post('v2/stripe/add-coupon', {uid: this.uid, weekDate: this.weekDate, coupon: code, reason}));
        } else {
          ({data} = await api.post(`v2/stripe/coupons/customer/${this.uid}/apply`, {coupon: code, reason}));
        }
        this.$store.commit('showSnackbar', {message: this.successMessage(code, data), color: 'success', timeout: 6000});
        this.$emit('applied', data);
        this.close();
      } catch (e) {
        this.applyError = this.messageOf(e, 'Failed to apply the coupon');
      } finally {
        this.applying = false;
      }
    },

    successMessage(code, data) {
      if (this.weekDate) {
        const invoice = (data.adjustments || []).find(a => a.type === 'invoice');
        const paid = invoice && invoice.status === 'paid' ? ', paid' : (invoice && invoice.payment_failure ? ', payment failed' : '');
        return data.action === 'created_with_coupon'
          ? `Invoice created with coupon ${code}${paid}`
          : `Invoice reissued with coupon ${code}${paid}`;
      }
      if (data.result && data.result.isGiftCard) {
        return `Gift card ${code} redeemed: ${formatCurrency(data.result.amount_off)} added to the customer's credit`;
      }
      return `Coupon ${code} applied to the customer`;
    },

    valueText(c) {
      if (c.percent_off != null) return `${c.percent_off}% off`;
      if (c.amount_off != null) return `${formatCurrency(c.amount_off)} off${c.currency && c.currency !== 'cad' ? ` (${c.currency.toUpperCase()})` : ''}`;
      return '';
    },

    durationText(c) {
      if (c.duration === 'repeating') return `${c.duration_in_months} months`;
      return c.duration;
    },

    messageOf(e, fallback) {
      return (e.response && e.response.data && (e.response.data.message || e.response.data.error)) || e.message || fallback;
    },
  },
};
</script>
